import { HttpClient } from 'api/http-client';
import {
  CreateSignalPayload,
  GetSignalConfigurationFromPayload,
  SignalConfiguration,
  TemporarySignalConfiguration,
} from './types';

export const reasonOptions = [
  {
    color: 'red',
    label: 'Projects',
    description:
      'Issue with enabling users to create and manage multiple projects, set up project templates, track timelines, and handle project-level operations.',
    descriptionEdited: false,
    value: 1,
    position: 1,
    active: true,
    deletable: true,
    editable: true,
    aiGenerated: true,
  },
  {
    color: 'blue',
    label: 'Tasks',
    description:
      'Issue with managing project tasks, assigning resources, setting dependencies, and tracking task progress. Includes task properties, task pane, and drag-and-drop functionality.',
    descriptionEdited: false,
    value: 2,
    position: 2,
    active: true,
    deletable: true,
    editable: true,
    aiGenerated: true,
  },
  {
    color: 'green',
    label: 'Phases',
    description:
      'Issue with managing project phases, milestones, deadlines, and task groupings within projects.',
    descriptionEdited: false,
    value: 3,
    position: 3,
    active: true,
    deletable: true,
    editable: true,
    aiGenerated: true,
  },
  {
    color: 'yellow',
    label: 'Project Plan (Board View and Timeline View)',
    description:
      'Issue with visualizing project progress using board view or timeline view (Gantt charts). Includes tracking dependencies, shifting timelines, and presenting project plans.',
    descriptionEdited: false,
    value: 4,
    position: 4,
    active: true,
    deletable: true,
    editable: true,
    aiGenerated: false,
  },
];
export const moreReasonOptions = [
  {
    color: 'purple',
    label: 'Templates',
    description:
      'Issue with pre-built templates for standardizing project plans, phases, and tasks. Includes customizing templates for repeatable frameworks.',
    descriptionEdited: false,
    value: 5,
    position: 5,
    active: true,
    deletable: true,
    editable: true,
    aiGenerated: true,
  },
  {
    color: 'yellow',
    label: 'Playbook Automation',
    description:
      'Issue with automating workflows using playbooks that define actions, triggers, and rules for consistency.',
    descriptionEdited: false,
    value: 6,
    position: 6,
    active: true,
    deletable: true,
    editable: true,
    aiGenerated: true,
  },
  {
    color: 'teal',
    label: 'Teammates',
    description:
      'Issue with managing team members, assigning roles, setting visibility, and controlling permissions.',
    descriptionEdited: false,
    value: 7,
    position: 7,
    active: true,
    deletable: true,
    editable: true,
    aiGenerated: true,
  },
  {
    color: 'pink',
    label: 'Spaces (Private and Shared)',
    description:
      'Issue with document collaboration in private or shared spaces. Includes creating, sharing, and securing documents.',
    descriptionEdited: false,
    value: 8,
    position: 8,
    active: true,
    deletable: true,
    editable: true,
    aiGenerated: true,
  },
];
export const signalResponse = {
  signalConfigurationId: 1,
  signalName: 'Escalations',
  signalDetectionInstructions:
    'Usability concerns are issues that impact the ease of use, efficiency, and satisfaction of interacting with the product. Analyze the following meeting transcript to Detect usability concerns raised by participants. Usability concerns are issues that impact the ease of use, efficiency, and satisfaction of interacting with the product. Look for specific mentions of: Navigation Issues: Difficulties in finding features or completing tasks. Complexity: Complaints about the product being too complicated or overwhelming. Performance Problems: Instances of slow or lagging functionality.',
  instructionsEdited: false,
  shouldCreateTask: true,
  fields: [
    {
      fieldId: 1660316,
      metaData: {
        options: reasonOptions,
      },
    },
  ],
  knowledgeSources: [],
};

class SignalAPIClient extends HttpClient {
  public async getAllSignalConfigurations(): Promise<SignalConfiguration[]> {
    return this.get<SignalConfiguration[]>('/signals/configurations/list');
  }
  public async getSignalConfiguration(
    signalId: number,
  ): Promise<SignalConfiguration> {
    return this.get<SignalConfiguration>(`/signals/configurations/${signalId}`);
  }
  /**
   * Fetch signal configuration along with all associated details (conversations, outputs, etc.)
   * as supported by the backend via includeAllDetails=true.
   *
   * NOTE: We intentionally type this loosely because the backend shape may evolve.
   * The UI normalizes the pieces it needs (conversations + output files).
   */
  public async getSignalConfigurationAllDetails(
    signalId: number,
  ): Promise<any> {
    return this.get<any>(`/signals/configurations/${signalId}`, {
      params: { includeAllDetails: true },
    });
  }
  public async createSignalConfigurations(
    payload: CreateSignalPayload,
  ): Promise<SignalConfiguration> {
    return this.post<SignalConfiguration, CreateSignalPayload>(
      '/signals/configurations',
      payload,
    );
  }
  public async updateSignalConfigurations(
    payload: SignalConfiguration,
  ): Promise<SignalConfiguration> {
    return this.put<SignalConfiguration, CreateSignalPayload>(
      `/signals/configurations/${payload.signalConfigurationId}`,
      payload,
    );
  }
  public async deleteSignalConfiguration(signalId: number): Promise<void> {
    return this.delete<void>(`/signals/configurations/${signalId}`);
  }
  public async getSignalConfigurationPrompt(
    payload: GetSignalConfigurationFromPayload,
  ): Promise<TemporarySignalConfiguration> {
    return this.post<TemporarySignalConfiguration, { signalName: string }>(
      '/signals/configurations/generate-meta-prompt',
      payload,
    );
  }
}

export const SignalAPI = new SignalAPIClient();
